import { ChemicalPalettePicker } from '../components/ChemicalPalettePicker';
import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadBlob, downloadText } from '@peruselab/structure-editor/domain/download';
import { Icon } from '../components/Icon';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { useFocusTrap } from '../hooks/useFocusTrap';
import {
  DPI_PRESETS,
  FILE_EXTENSION,
  MEDIA_TYPE,
  WHITE,
  TRANSPARENT,
  defaultSettings,
  exportBlockers,
  fromUnit,
  isRaster,
  pixelSize,
  resize,
  supportsTransparency,
  toUnit,
  withFormat,
  type ExportFormat,
  type ExportSettings
} from './exportModel';
import { prepareExport, rasterize, type ExportSource, type PreparedExport } from './exportPipeline';
import { svgToPdf } from './pdfExport';
import { pageOf } from '@peruselab/structure-editor/domain/page';

const FORMATS: readonly ExportFormat[] = ['svg', 'png', 'jpeg', 'pdf'];
// Room around the fitted image in the preview, in CSS px.
const PREVIEW_INSET = 32;

// Mounted only while open (WorkspaceShell), so nothing here runs while it is closed.
interface ExportDialogProps {
  readonly source: ExportSource;
  readonly fileStem: string;
  readonly onClose: () => void;
}

// #758 -- export preview (docs/model/drawing-ui-export.md): format, scope, background, size,
// padding and resolution side by side with a live preview of exactly what will be written. The
// preview is regenerated on every settings change; Export is enabled only when the preview on screen
// matches the current settings and nothing blocks it. Closing never changes the drawing.
export function ExportDialog({ source, fileStem, onClose }: ExportDialogProps) {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const formatRef = useRef<HTMLButtonElement | null>(null);
  const [settings, setSettings] = useState<ExportSettings>(() => {
    const settings = defaultSettings('svg', pageOf(source.document).unit);
    // #1140: the whole drawing by default -- but a blank paper has none, so it starts on its page.
    const blank = source.document.structure.graph.nodes.length === 0 && source.document.annotations.length === 0;
    return blank ? { ...settings, scope: 'page' } : settings;
  });
  const [prepared, setPrepared] = useState<{
    readonly key: string;
    readonly document: ExportSource['document'];
    readonly selection: ExportSource['selection'];
    readonly result: PreparedExport;
  } | null>(null);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState({ zoom: 1, x: 0, y: 0 });
  const dragRef = useRef<{ x: number; y: number; startX: number; startY: number } | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [previewBox, setPreviewBox] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const element = previewRef.current;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setPreviewBox({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEscapeKey(true, onClose);
  useFocusTrap(true, dialogRef);
  useEffect(() => {
    formatRef.current?.focus();
  }, []);

  // A preview is current only for these settings, this drawing and this selection (by identity:
  // any edit makes a new document). A stale preview can never be exported.
  const key = JSON.stringify(settings);
  const { document: drawing, selection, renderSvg } = source;
  useEffect(() => {
    let current = true;
    void prepareExport({ document: drawing, selection, renderSvg }, JSON.parse(key) as ExportSettings).then(
      (result) => current && setPrepared({ key, document: drawing, selection, result }),
      (failure: unknown) => current && setError(failure instanceof Error ? failure.message : String(failure))
    );
    return () => {
      current = false;
    };
  }, [key, drawing, selection, renderSvg]);

  // The SVG shown in the preview, as an <img> (never inlined: it carries authored text).
  const previewUrl = useMemo(() => {
    if (!prepared) return null;
    return URL.createObjectURL(new Blob([prepared.result.svg], { type: 'image/svg+xml;charset=utf-8' }));
  }, [prepared]);
  useEffect(() => () => void (previewUrl && URL.revokeObjectURL(previewUrl)), [previewUrl]);

  const result = prepared?.result ?? null;
  const ready = prepared?.key === key && prepared.document === drawing && prepared.selection === selection;
  const sizeMm = result?.sizeMm ?? { width: 0, height: 0 };
  const blockers = result ? exportBlockers(settings, sizeMm, result) : [];
  const canExport = ready && blockers.length === 0 && !exporting;
  const unit = settings.unit;
  const shown = (mm: number) => Number(toUnit(mm, unit).toFixed(unit === 'mm' ? 1 : 2));
  const update = (patch: Partial<ExportSettings>) => setSettings((current) => ({ ...current, ...patch }));
  const missing = [...new Set(result?.text?.missing.flatMap((entry) => entry.characters) ?? [])];
  const pixels = pixelSize(sizeMm, settings.dpi);
  // #772 -- the image box is exactly the output's shape, fitted to the preview, so its outline shows
  // where the file ends (a transparent page would otherwise be invisible on the checkerboard).
  const fit = Math.min(
    (previewBox.width - PREVIEW_INSET) / (sizeMm.width || 1),
    (previewBox.height - PREVIEW_INSET) / (sizeMm.height || 1)
  );
  const imageSize =
    fit > 0 && sizeMm.width > 0 && sizeMm.height > 0
      ? { width: sizeMm.width * fit, height: sizeMm.height * fit }
      : null;

  const doExport = async () => {
    if (!result || !canExport) return;
    setExporting(true);
    setError(null);
    try {
      const fileName = `${fileStem}.${FILE_EXTENSION[settings.format]}`;
      if (settings.format === 'svg') downloadText(fileName, result.svg, MEDIA_TYPE.svg);
      else if (settings.format === 'png' || settings.format === 'jpeg') {
        downloadBlob(fileName, await rasterize(result.svg, result.sizeMm, settings.dpi, settings.format));
      } else {
        downloadBlob(fileName, await svgToPdf(result.svg, result.sizeMm, `${import.meta.env.BASE_URL}fonts/export`));
      }
      onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setExporting(false);
    }
  };

  const panStart = (event: ReactPointerEvent<HTMLDivElement>) => {
    dragRef.current = { x: event.clientX, y: event.clientY, startX: view.x, startY: view.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const panMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (drag)
      setView((v) => ({ ...v, x: drag.startX + event.clientX - drag.x, y: drag.startY + event.clientY - drag.y }));
  };
  const zoomBy = (factor: number) => setView((v) => ({ ...v, zoom: Math.min(8, Math.max(0.25, v.zoom * factor)) }));

  return (
    <div
      className="export-dialog-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="export-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="export-dialog-title"
        data-testid="export-dialog"
      >
        <div className="export-dialog-head">
          <h2 id="export-dialog-title">{t('exportDialog.title')}</h2>
          <button
            type="button"
            className="icon-button"
            aria-label={t('exportDialog.cancel')}
            title={t('exportDialog.cancel')}
            onClick={onClose}
          >
            <Icon name="close" />
          </button>
        </div>

        <div className="export-dialog-body">
          <div className="export-preview-column">
            <div
              ref={previewRef}
              className={`export-preview${settings.background.kind === 'transparent' ? ' transparent' : ''}`}
              role="img"
              aria-label={t('exportDialog.previewLabel')}
              aria-busy={!ready}
              data-ready={ready || undefined}
              onPointerDown={panStart}
              onPointerMove={panMove}
              onPointerUp={() => (dragRef.current = null)}
              onPointerCancel={() => (dragRef.current = null)}
              onWheel={(event) => setView((v) => ({ ...v, x: v.x - event.deltaX, y: v.y - event.deltaY }))}
            >
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt=""
                  draggable={false}
                  style={{
                    ...imageSize,
                    transform: `translate(${view.x}px, ${view.y}px) scale(${view.zoom})`
                  }}
                />
              ) : null}
              {!ready ? <span className="export-preview-status">{t('exportDialog.updating')}</span> : null}
            </div>
            <div className="export-preview-tools">
              <button
                type="button"
                className="icon-button"
                aria-label={t('exportDialog.zoomIn')}
                title={t('exportDialog.zoomIn')}
                onClick={() => zoomBy(1.25)}
              >
                <Icon name="add" />
              </button>
              <button
                type="button"
                className="icon-button"
                aria-label={t('exportDialog.zoomOut')}
                title={t('exportDialog.zoomOut')}
                onClick={() => zoomBy(1 / 1.25)}
              >
                <Icon name="remove" />
              </button>
              <button
                type="button"
                className="icon-button"
                aria-label={t('exportDialog.fit')}
                title={t('exportDialog.fit')}
                onClick={() => setView({ zoom: 1, x: 0, y: 0 })}
              >
                <Icon name="fitScreen" />
              </button>
              <span className="export-preview-note">{t('exportDialog.previewNote')}</span>
            </div>
          </div>

          <div className="export-controls">
            <fieldset>
              <legend>{t('exportDialog.format')}</legend>
              <div className="export-segmented" role="radiogroup" aria-label={t('exportDialog.format')}>
                {FORMATS.map((format, index) => (
                  <button
                    key={format}
                    ref={index === 0 ? formatRef : undefined}
                    type="button"
                    role="radio"
                    aria-checked={settings.format === format}
                    className={settings.format === format ? 'active' : ''}
                    onClick={() => setSettings((current) => withFormat(current, format))}
                  >
                    {t(`exportDialog.formats.${format}`)}
                  </button>
                ))}
              </div>
            </fieldset>

            <fieldset>
              <legend>{t('exportDialog.scope')}</legend>
              {(['page', 'whole', 'selection'] as const).map((scope) => (
                <label key={scope} className="export-radio">
                  <input
                    type="radio"
                    name="export-scope"
                    checked={settings.scope === scope}
                    disabled={scope === 'selection' && !!result?.selectionEmpty}
                    onChange={() => update({ scope, sizeMm: null })}
                  />
                  {t(`exportDialog.scopes.${scope}`)}
                </label>
              ))}
            </fieldset>

            <fieldset>
              <legend>{t('exportDialog.background')}</legend>
              {supportsTransparency(settings.format) ? (
                <label className="export-radio">
                  <input
                    type="radio"
                    name="export-background"
                    checked={settings.background.kind === 'transparent'}
                    onChange={() => update({ background: TRANSPARENT })}
                  />
                  {t('exportDialog.transparent')}
                </label>
              ) : null}
              <label className="export-radio">
                <input
                  type="radio"
                  name="export-background"
                  checked={settings.background.kind === 'color'}
                  onChange={() => update({ background: WHITE })}
                />
                {t('exportDialog.solid')}
                <input
                  type="color"
                  aria-label={t('exportDialog.backgroundColor')}
                  value={settings.background.kind === 'color' ? settings.background.color : '#ffffff'}
                  onChange={(event) => update({ background: { kind: 'color', color: event.target.value } })}
                />
              </label>
              <ChemicalPalettePicker
                value={settings.background.kind === 'color' ? settings.background.color : '#ffffff'}
                onSelect={(color) => update({ background: { kind: 'color', color } })}
              />
            </fieldset>

            <fieldset>
              <legend>{t('exportDialog.size')}</legend>
              <div className="export-size-row">
                <label>
                  {t('exportDialog.width')}
                  <input
                    type="number"
                    min={0}
                    step="any"
                    value={result ? shown(sizeMm.width) : ''}
                    onChange={(event) =>
                      result &&
                      update({
                        sizeMm: resize(
                          result.naturalMm,
                          sizeMm,
                          'width',
                          fromUnit(Number(event.target.value), unit),
                          settings.lockProportions
                        )
                      })
                    }
                  />
                </label>
                <button
                  type="button"
                  className="export-proportion-link"
                  aria-pressed={settings.lockProportions}
                  aria-label={t('exportDialog.lock')}
                  title={t('exportDialog.lock')}
                  onClick={() => update({ lockProportions: !settings.lockProportions })}
                >
                  <Icon name="link" />
                </button>
                <label>
                  {t('exportDialog.height')}
                  <input
                    type="number"
                    min={0}
                    step="any"
                    value={result ? shown(sizeMm.height) : ''}
                    onChange={(event) =>
                      result &&
                      update({
                        sizeMm: resize(
                          result.naturalMm,
                          sizeMm,
                          'height',
                          fromUnit(Number(event.target.value), unit),
                          settings.lockProportions
                        )
                      })
                    }
                  />
                </label>
                <select
                  aria-label={t('exportDialog.unit')}
                  value={unit}
                  onChange={(event) => update({ unit: event.target.value as 'mm' | 'in' })}
                >
                  <option value="mm">mm</option>
                  <option value="in">in</option>
                </select>
              </div>
              <button
                type="button"
                className="export-link"
                disabled={!settings.sizeMm}
                onClick={() => update({ sizeMm: null })}
              >
                {t('exportDialog.naturalSize')}
              </button>
              {settings.scope === 'page' ? null : (
                <label className="export-padding">
                  {t('exportDialog.padding', { unit })}
                  <input
                    type="number"
                    min={0}
                    step="any"
                    value={shown(settings.paddingMm)}
                    onChange={(event) =>
                      update({ paddingMm: Math.max(0, fromUnit(Number(event.target.value) || 0, unit)), sizeMm: null })
                    }
                  />
                </label>
              )}
            </fieldset>

            {isRaster(settings.format) ? (
              <fieldset>
                <legend>{t('exportDialog.resolution')}</legend>
                <select
                  aria-label={t('exportDialog.dpi')}
                  value={DPI_PRESETS.includes(settings.dpi as 300 | 600) ? String(settings.dpi) : 'custom'}
                  onChange={(event) =>
                    update({ dpi: event.target.value === 'custom' ? 150 : Number(event.target.value) })
                  }
                >
                  {DPI_PRESETS.map((dpi) => (
                    <option key={dpi} value={dpi}>
                      {dpi} DPI
                    </option>
                  ))}
                  <option value="custom">{t('exportDialog.customDpi')}</option>
                </select>
                {!DPI_PRESETS.includes(settings.dpi as 300 | 600) ? (
                  <input
                    type="number"
                    min={36}
                    max={2400}
                    aria-label={t('exportDialog.dpi')}
                    value={settings.dpi}
                    onChange={(event) => update({ dpi: Number(event.target.value) })}
                  />
                ) : null}
                <p className="export-hint" data-testid="export-pixels">
                  {t('exportDialog.pixels', { width: pixels.width, height: pixels.height })}
                </p>
              </fieldset>
            ) : null}

            <div className="export-notices" aria-live="polite">
              {settings.scope === 'page' && result?.clipped ? (
                <p className="export-notice warning" data-testid="export-clipped">
                  {t('exportDialog.clipped')}
                </p>
              ) : null}
              {missing.length ? (
                <p className="export-notice">{t('exportDialog.missingGlyphs', { characters: missing.join(' ') })}</p>
              ) : null}
              {result?.text?.substitutions.map((entry) => (
                <p key={entry.from} className="export-notice">
                  {t('exportDialog.substitution', entry)}
                </p>
              ))}
              {result?.fontsUnavailable ? <p className="export-notice">{t('exportDialog.fontsUnavailable')}</p> : null}
              {blockers.map((blocker) => (
                <p key={blocker} className="export-notice error" role="alert">
                  {t(`exportDialog.blockers.${blocker}`)}
                </p>
              ))}
              {error ? (
                <p className="export-notice error" role="alert">
                  {error}
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <div className="export-dialog-foot">
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label={t('exportDialog.cancel')}
            title={t('exportDialog.cancel')}
          >
            <Icon name="close" />
          </button>
          <button
            type="button"
            className="icon-button"
            disabled={!canExport}
            onClick={() => void doExport()}
            aria-label={t('exportDialog.export', { format: t(`exportDialog.formats.${settings.format}`) })}
            title={t('exportDialog.export', { format: t(`exportDialog.formats.${settings.format}`) })}
          >
            <Icon name="download" />
          </button>
        </div>
      </div>
    </div>
  );
}
